/* eslint-disable react/function-component-definition */
import React, { useContext, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import { Label } from 'reactstrap';
import { SpinnerDotted } from 'spinners-react';
import { toast, ToastContainer } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { Entry } from '../../redux/entrySlice';
import SelectBox from '../../components/SelectBox/SelectBox';
import PhoneNumberInput from '../../components/PhoneNumberBox/PhoneNumberBox';
import styles from './editUser.module.scss';
import { TableContext } from '../../contexts/tableContext';
import MultipleFileUpload from '../../components/MultipleFileUpload/MultipleFileUpload';
import DatePickerComponent from '../../components/DatePicker/DatePicker';
import 'react-toastify/dist/ReactToastify.css';
import TextInput from '../../components/TextField/TextField';
import EmailInput from '../../components/EmailBox/EmailBox';
import { REACT_APP_API_ROLES_DROPDOWN, SUB_ADMIN_ROLE_ID } from '../../redux/apiConstants';
import PermissionMatrix from '../../components/PermissionMatrix/PermissionMatrix';
import { hasAnyMenuPermission } from '../../components/PermissionMatrix/permissionUtils';

const UserEdit = React.memo(() => {
  const [submiting, setSubmitting] = useState(false);
  const { dashboardStyle } = useContext(TableContext);
  const { bodyStyle } = useContext(TableContext);
  const { formthemeStyle } = useContext(TableContext);
  const { setDashboardHeader } = useContext(TableContext);
  const { errormsgStyle } = useContext(TableContext);
  const params = useParams();
  const location = useLocation();
  const userApiBase = location.pathname.includes('/sub-admins/') ? 'sub-admins' : 'user';
  const isSubAdmin = userApiBase === 'sub-admins';
  const [user, setUser] = useState([]);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const dispatch = useDispatch();
  const { files } = useContext(TableContext);
  const permissionMatrixRef = useRef(null);
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setError: setFieldError,
    clearErrors,
  } = useForm();

  useEffect(() => {
    window.scrollTo(0, 0);
    setDashboardHeader(isSubAdmin ? 'Edit sub admin' : 'Edit User Details');

    if (!isSubAdmin) {
      const url2 = REACT_APP_API_ROLES_DROPDOWN;
      fetch(url2)
        .then((resp) => resp.json())
        .then((resp) => {
          setRoles(resp.data.role);
        });
    }

    const actionData = {
      actionUrl: `${userApiBase}/${params.userId}`,
      actionMethod: 'get',
    };
    dispatch(Entry(actionData)).then((resp) => {
      setUser(resp.payload.data.user);
      reset(resp.payload.data.user);
    });
  }, [params.userId, userApiBase, isSubAdmin, dispatch, reset, setDashboardHeader]);

  function onSubmitdata(data) {
    if (data.name.trim().length === 0) {
      document.getElementById('name').focus();
      return;
    }

    if (isSubAdmin) {
      const menuPermissions = permissionMatrixRef.current?.getPermissions?.() || [];
      if (!hasAnyMenuPermission(menuPermissions)) {
        setError('Select at least one dashboard menu for this sub-admin');
        return;
      }
    }

    setSubmitting(true);
    setError(null);
    clearErrors(['phone', 'email']);

    const apiData = {
      name: data.name,
      email: data.email,
      phone: data.phone,
      role: isSubAdmin ? SUB_ADMIN_ROLE_ID : data.role.id ? data.role.id : data.role.value,
      isVerified: true,
      File: files,
    };

    if (!isSubAdmin) {
      apiData.dateOfBirth = moment(data.dateOfBirth).format('MM/DD/YYYY');
    }

    if (isSubAdmin) {
      const menuPermissions = permissionMatrixRef.current?.getPermissions?.() || [];
      apiData.menuPermissions = files?.length
        ? JSON.stringify(menuPermissions)
        : menuPermissions;
    }

    const payload = {
      actionUrl: `${userApiBase}/${params.userId}`,
      actionMethod: 'patch',
      apiData,
    };

    dispatch(Entry(payload)).then((resp) => {
      setSubmitting(false);
      const body = resp?.payload;
      const isSuccess =
        Entry.fulfilled.match(resp) &&
        (body?.success === true || body?.code === 200 || body?.code === '200');

      if (isSuccess) {
        navigate(-1);
        toast.success(isSubAdmin ? 'Sub admin updated successfully' : 'User updation successfull');
        return;
      }

      const message = body?.message || 'Something went wrong!';
      const lowerMessage = String(message).toLowerCase();

      if (lowerMessage.includes('phone') && lowerMessage.includes('taken')) {
        setFieldError('phone', { type: 'server', message });
        return;
      }

      if (lowerMessage.includes('email') && lowerMessage.includes('taken')) {
        setFieldError('email', { type: 'server', message });
        return;
      }

      if (body?.code === 401 || body?.code === 400 || body?.code === 403) {
        setError(message);
        toast.error('Error');
      } else {
        navigate(-1);
      }
    });
  }

  return (
    <div className={styles[bodyStyle]}>
      <div
        className={styles[dashboardStyle]}
        style={{ minHeight: '100vh' }}
        id={styles.dashboardcont2}
      >
        <div className={styles[formthemeStyle]} id={styles.addform}>
          <FontAwesomeIcon
            icon={faArrowLeft}
            value="Back"
            onClick={() => {
              navigate(-1);
            }}
            className={styles.arrowback}
          />
          <div className="container-fluid mt-5 edituserform">
            {user.length !== 0 ? (
              <form onSubmit={handleSubmit(onSubmitdata)}>
                <div className="row">
                  <div className="col-md-6 col-sm-6">
                    {user && (
                      <div>
                        <TextInput
                          className={styles.inputbox}
                          classNamedark={styles.inputbox1}
                          labelstyle={styles.formlabel}
                          label="Name"
                          fieldName="name"
                          placeHolder="Enter Name"
                          register={register}
                          errors={errors}
                          isRequired
                          mandatory
                        />
                      </div>
                    )}
                  </div>
                  <div className="col-md-6 col-sm-6">
                    <EmailInput
                      className={styles.inputbox}
                      classNamedark={styles.inputbox1}
                      labelstyle={styles.formlabel}
                      label="Email"
                      fieldName="email"
                      register={register}
                      errors={errors}
                      isRequired
                      mandatory
                      placeHolder="Email"
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6 col-sm-6">
                    <PhoneNumberInput
                      labelstyle={styles.formlabel}
                      classNamedark={styles.inputbox1}
                      label="Phone Number"
                      placeHolder="Phone Number"
                      className={styles.inputbox}
                      fieldName="phone"
                      register={register}
                      errors={errors}
                      isRequired
                      mandatory
                    />
                  </div>
                  <div className="col-md-6 col-sm-6">
                    {isSubAdmin ? (
                      <div>
                        <p className={styles.formlabel}>
                          Role: <strong>Sub-Admin</strong>
                        </p>
                        <p style={{ fontSize: '0.875rem', color: '#6c757d', marginTop: 4 }}>
                          This account uses the Sub-Admin role.
                        </p>
                      </div>
                    ) : (
                      <SelectBox
                        className={styles.inputbox}
                        classNamedark={styles.inputbox1}
                        name="role"
                        label="Role"
                        labelstyle={styles.formlabel}
                        control={control}
                        register={register}
                        values={roles.selectdata}
                        errors={errors}
                        placeholder="Choose Role"
                        defaultvalue={user.role.id}
                        mandatory
                      />
                    )}
                  </div>
                </div>
                {!isSubAdmin && (
                  <div className="row">
                    <div className="col-md-6 col-sm-6">
                      <Label className={styles.formlabel}>Date of Birth</Label>
                      <span style={{ color: 'red', marginLeft: '3px' }}>*</span>
                      <DatePickerComponent
                        className={styles.dateinputbox}
                        classNamedark={styles.dateinputbox1}
                        control={control}
                        name="dateOfBirth"
                        isRequired
                        errors={errors}
                        label="Date Of Birth"
                      />
                    </div>
                  </div>
                )}
                <div className="row mt-3">
                  <div className="col-md-6 col-sm-6">
                    <MultipleFileUpload editFiles={user.files} section="user" />
                  </div>
                </div>
                {isSubAdmin && (
                  <PermissionMatrix
                    ref={permissionMatrixRef}
                    disabled={submiting}
                    modulesActionUrl="sub-admins/menu-modules"
                    simpleSelection
                    initialPermissions={user.menuPermissions}
                  />
                )}
                <input
                  className={styles.formbtn}
                  type="submit"
                  disabled={submiting}
                  value={submiting ? 'Please wait..' : 'Update'}
                />
                {error && <h6 className={styles[errormsgStyle]}>{error}</h6>}
              </form>
            ) : (
              <div style={{ textAlign: 'center', marginTop: '130px', color: '#39979d' }}>
                <SpinnerDotted />
              </div>
            )}
            <br />
            <ToastContainer autoClose={2000} />
          </div>
        </div>
      </div>
    </div>
  );
});

export default UserEdit;
